import { useMemo, useCallback } from 'react'
import {
  type ColumnDef,
  type VisibilityState,
  type ColumnSizingState,
  type OnChangeFn,
} from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Users, Package, Download, Eye, Power } from 'lucide-react'
import {
  DataTable,
  ColumnVisibilityMenu,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
} from '@/components/ui/data-table'
import { useTablePreferences } from '@/hooks/useTablePreferences'
import { useToggleServicioEstado } from '@/hooks/useServicios'
import { usePermissions } from '@/hooks/usePermissions'
import type { ServicioConClientes } from '@/services/serviciosService'
import { agruparPorPeriodicidad } from './agruparPorPeriodicidad'
import {
  CATEGORIA_SERVICIO_LABELS,
  PERIODICIDAD_SERVICIO_LABELS,
  type CategoriaServicio,
  type PeriodicidadServicio,
  type EstadoServicio,
} from '@/types/database'
import { toast } from 'sonner'

interface ServiciosTableProps {
  servicios: ServicioConClientes[]
  isLoading?: boolean
  onEditar: (servicio: ServicioConClientes) => void
  onEliminar: (servicio: ServicioConClientes) => void
  onVerClientes: (servicio: ServicioConClientes) => void
  onVerDetalle?: (servicio: ServicioConClientes) => void
  clientesPreview?: Map<string, string[]>
  canEdit?: boolean
  canDelete?: boolean
}

// Tipo extendido para acceder a campos opcionales
type ServicioExtendido = ServicioConClientes & {
  descripcion?: string
  categoria?: CategoriaServicio
  precio_estimado?: number | null
  periodicidad?: PeriodicidadServicio | null
  estado?: EstadoServicio
}

// Formatear precio
const formatPrecio = (precio: number | null | undefined): string => {
  if (precio === null || precio === undefined) return '-'
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(precio)
}

// Exportar a CSV
// Sin verHonorariosCliente la columna de precio no se exporta.
const exportToCSV = (servicios: ServicioConClientes[], incluirPrecio: boolean) => {
  const headers = ['Nombre', 'Categoria', 'Descripcion', 'Precio Estimado', 'Periodicidad', 'Estado', 'Clientes']

  const rows = servicios.map((s) => {
    const ext = s as ServicioExtendido
    return [
      s.nombre || '',
      ext.categoria ? CATEGORIA_SERVICIO_LABELS[ext.categoria] : '',
      ext.descripcion || '',
      ext.precio_estimado?.toString() || '0',
      ext.periodicidad ? PERIODICIDAD_SERVICIO_LABELS[ext.periodicidad] : '',
      ext.estado || 'activo',
      s.clientesCount.toString(),
    ]
  })

  const sinPrecio = (fila: string[]) => (incluirPrecio ? fila : fila.filter((_, i) => i !== 3))
  const csvContent = [
    sinPrecio(headers).join(';'),
    ...rows.map((row) => sinPrecio(row).map((cell) => `"${cell.replace(/"/g, '""')}"`).join(';')),
  ].join('\n')

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `servicios_${new Date().toISOString().split('T')[0]}.csv`
  link.click()
  URL.revokeObjectURL(url)

  toast.success(`${servicios.length} servicios exportados`)
}

export function ServiciosTable({
  servicios,
  isLoading,
  onEditar,
  onEliminar,
  onVerClientes,
  onVerDetalle,
  canEdit = true,
  canDelete = true,
  clientesPreview,
}: ServiciosTableProps) {
  // Hook de preferencias de tabla con column sizing
  const {
    visibility,
    saveVisibility,
    columnSizing,
    saveColumnSizing,
    resetVisibility,
    resetColumnSizing,
    resetAll,
  } = useTablePreferences('servicios', {
    defaultVisibility: {
      nombre: true,
      categoria: true,
      descripcion: true,
      precio: true,
      periodicidad: true,
      clientes: true,
    },
  })

  // Hook para toggle de estado
  const toggleEstado = useToggleServicioEstado()
  const verPrecio = usePermissions().puede.verHonorariosCliente === true

  // Handler para toggle de estado desde menú contextual
  const handleToggleEstado = useCallback(
    (servicio: ServicioConClientes) => {
      const ext = servicio as ServicioExtendido
      const isActivo = ext.estado === 'activo'
      const nuevoEstado = isActivo ? 'inactivo' : 'activo'
      toggleEstado.mutate(
        { id: servicio.id, nuevoEstado },
        {
          onSuccess: () => {
            toast.success(`Servicio ${isActivo ? 'desactivado' : 'activado'}`)
          },
          onError: () => {
            toast.error('Error al cambiar estado del servicio')
          },
        }
      )
    },
    [toggleEstado]
  )

  // Definición de columnas
  const columns = useMemo<ColumnDef<ServicioConClientes>[]>(
    () => [
      {
        id: 'nombre',
        accessorKey: 'nombre',
        header: 'Nombre',
        meta: { label: 'Nombre' },
        size: 200,
        minSize: 120,
        maxSize: 350,
        cell: ({ row }) => {
          const ext = row.original as ServicioExtendido
          const isInactivo = ext.estado === 'inactivo'
          return (
            <div className="flex items-center gap-2">
              <span className={`font-medium ${isInactivo ? 'text-muted-foreground' : ''}`}>
                {row.original.nombre}
              </span>
              {isInactivo && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  Inactivo
                </Badge>
              )}
            </div>
          )
        },
      },
      {
        id: 'categoria',
        accessorFn: (row) => {
          const ext = row as ServicioExtendido
          return ext.categoria ? CATEGORIA_SERVICIO_LABELS[ext.categoria] : ''
        },
        header: 'Categoria',
        meta: { label: 'Categoria' },
        size: 150,
        minSize: 100,
        maxSize: 220,
        cell: ({ row }) => {
          const ext = row.original as ServicioExtendido
          if (!ext.categoria) return null
          return (
            <Badge variant="secondary">
              {CATEGORIA_SERVICIO_LABELS[ext.categoria]}
            </Badge>
          )
        },
      },
      {
        id: 'descripcion',
        accessorFn: (row) => (row as ServicioExtendido).descripcion || '',
        header: 'Descripcion',
        meta: { label: 'Descripcion' },
        size: 250,
        minSize: 150,
        maxSize: 400,
        enableSorting: false,
        cell: ({ row }) => {
          const ext = row.original as ServicioExtendido
          return (
            <span className="text-muted-foreground max-w-md truncate block">
              {ext.descripcion || '-'}
            </span>
          )
        },
      },
      {
        id: 'precio',
        accessorFn: (row) => (row as ServicioExtendido).precio_estimado ?? 0,
        header: () => <div className="text-right">Precio Est.</div>,
        meta: { label: 'Precio Estimado' },
        size: 120,
        minSize: 90,
        maxSize: 180,
        cell: ({ row }) => {
          const ext = row.original as ServicioExtendido
          return (
            <div className="text-right font-mono tabular-nums">
              {formatPrecio(ext.precio_estimado)}
            </div>
          )
        },
      },
      {
        id: 'periodicidad',
        accessorFn: (row) => {
          const ext = row as ServicioExtendido
          return ext.periodicidad ? PERIODICIDAD_SERVICIO_LABELS[ext.periodicidad] : ''
        },
        header: 'Periodicidad',
        meta: { label: 'Periodicidad' },
        size: 120,
        minSize: 90,
        maxSize: 180,
        cell: ({ row }) => {
          const ext = row.original as ServicioExtendido
          return (
            <span className="text-sm text-muted-foreground">
              {ext.periodicidad
                ? PERIODICIDAD_SERVICIO_LABELS[ext.periodicidad]
                : '-'}
            </span>
          )
        },
      },
      {
        id: 'clientes',
        accessorFn: (row) => row.clientesCount,
        header: 'Clientes',
        meta: { label: 'Clientes' },
        size: 90,
        minSize: 70,
        maxSize: 130,
        cell: ({ row }) => {
          const servicio = row.original
          const preview = clientesPreview?.get(servicio.id) || []
          const hasClientes = servicio.clientesCount > 0

          const badgeContent = (
            <Badge
              variant={hasClientes ? 'default' : 'secondary'}
              className={hasClientes ? 'cursor-pointer hover:bg-primary/80' : ''}
              onClick={() => hasClientes && onVerClientes(servicio)}
            >
              <Users className="h-3 w-3 mr-1" />
              {servicio.clientesCount}
            </Badge>
          )

          if (!hasClientes || preview.length === 0) {
            return badgeContent
          }

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                {badgeContent}
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-medium text-xs">Clientes:</p>
                  <ul className="text-xs space-y-0.5">
                    {preview.slice(0, 3).map((nombre, i) => (
                      <li key={i}>{nombre}</li>
                    ))}
                    {servicio.clientesCount > 3 && (
                      <li className="text-muted-foreground">
                        +{servicio.clientesCount - 3} más...
                      </li>
                    )}
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          )
        },
      },
      {
        id: 'acciones',
        header: '',
        enableHiding: false,
        enableSorting: false,
        enableResizing: false,
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          const servicio = row.original
          return (
            <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onVerDetalle && (
                  <DropdownMenuItem onClick={() => onVerDetalle(servicio)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver detalle
                  </DropdownMenuItem>
                )}
                {canEdit && (
                  <DropdownMenuItem onClick={() => onEditar(servicio)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {servicio.clientesCount > 0 && (
                  <DropdownMenuItem onClick={() => onVerClientes(servicio)}>
                    <Users className="mr-2 h-4 w-4" />
                    Ver clientes ({servicio.clientesCount})
                  </DropdownMenuItem>
                )}
                {canEdit && (
                  <DropdownMenuItem
                    onClick={() => handleToggleEstado(servicio)}
                    disabled={toggleEstado.isPending}
                  >
                    <Power className="mr-2 h-4 w-4" />
                    {(servicio as ServicioExtendido).estado === 'activo' ? 'Desactivar' : 'Activar'}
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onEliminar(servicio)}
                      className="text-destructive focus:text-destructive"
                      disabled={servicio.clientesCount > 0}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [onEditar, onEliminar, onVerClientes, onVerDetalle, handleToggleEstado, toggleEstado.isPending, clientesPreview, canEdit, canDelete]
  )
  // Sin verHonorariosCliente la columna de precio no existe: ni se muestra ni se puede reactivar.
  const columnasVisibles = useMemo(
    () => (verPrecio ? columns : columns.filter((c) => c.id !== 'precio')),
    [columns, verPrecio]
  )

  // Instancia de tabla TanStack: sostiene el menú de columnas y el estado de
  // visibilidad/anchos que comparten todas las secciones de abajo, y renderiza
  // la tabla mientras carga o cuando no hay datos filtrados.
  const table = useReactTable({
    data: servicios,
    columns: columnasVisibles,
    state: {
      columnVisibility: visibility,
      columnSizing,
    },
    onColumnVisibilityChange: saveVisibility,
    onColumnSizingChange: saveColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
  })

  // Secciones por periodicidad: separan los servicios que generan honorarios
  // recurrentes de los que son a presupuesto.
  const grupos = useMemo(() => agruparPorPeriodicidad(servicios), [servicios])

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Toolbar con menú de columnas y exportar */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportToCSV(servicios, verPrecio)}
            disabled={servicios.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
          <ColumnVisibilityMenu
            table={table}
            onResetVisibility={resetVisibility}
            onResetColumnSizing={resetColumnSizing}
            onResetAll={resetAll}
          />
        </div>

        {/* Mientras carga o si no hay datos, un único estado; con datos, una tabla por sección */}
        {isLoading || servicios.length === 0 ? (
          <DataTable
            table={table}
            isLoading={isLoading}
            emptyMessage="No hay servicios registrados"
            emptyIcon={<Package className="h-6 w-6 text-muted-foreground" />}
            enableColumnResizing
          />
        ) : (
          <div className="space-y-6">
            {grupos.map((grupo) => (
              <ServiciosGrupoTable
                key={grupo.key}
                label={grupo.label}
                servicios={grupo.servicios}
                columns={columnasVisibles}
                visibility={visibility}
                columnSizing={columnSizing}
                saveVisibility={saveVisibility}
                saveColumnSizing={saveColumnSizing}
              />
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}

interface ServiciosGrupoTableProps {
  label: string
  servicios: ServicioConClientes[]
  columns: ColumnDef<ServicioConClientes>[]
  visibility: VisibilityState
  columnSizing: ColumnSizingState
  saveVisibility: OnChangeFn<VisibilityState>
  saveColumnSizing: OnChangeFn<ColumnSizingState>
}

// Una sección de la tabla, acotada a los servicios de una periodicidad. Usa las mismas
// columnas y preferencias que el resto; ordena alfabéticamente puertas adentro de la sección.
function ServiciosGrupoTable({
  label,
  servicios,
  columns,
  visibility,
  columnSizing,
  saveVisibility,
  saveColumnSizing,
}: ServiciosGrupoTableProps) {
  const table = useReactTable({
    data: servicios,
    columns,
    state: {
      columnVisibility: visibility,
      columnSizing,
    },
    onColumnVisibilityChange: saveVisibility,
    onColumnSizingChange: saveColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'nombre', desc: false }],
    },
  })

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
        {label}{' '}
        <span className="font-normal normal-case text-muted-foreground/70">
          ({servicios.length})
        </span>
      </h3>
      <DataTable table={table} emptyMessage="No hay servicios registrados" enableColumnResizing />
    </div>
  )
}
